import { useFetch } from "@/lib/axios.config";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export interface BankCard {
  id: string;
  masked_pan: string | null;
  owner: string | null;
  vendor: string | null;
  bank_id: string | null;
  expire_month: number | null;
  expire_year: number | null;
  card_name: string | null;
  is_default: boolean;
  is_active: boolean;
}

export interface BindCardInitPayload {
  card_number: string;
  expire_date: string;
}

export interface BindCardInitResponse {
  cid: string;
  otp_sent_to: string | null;
  resend_after_seconds: number;
}

export interface BindCardConfirmPayload {
  cid: string;
  otp: string;
  card_name?: string | null;
}

const CARDS_QUERY_KEY = ["cards"] as const;

export const useMyCards = () =>
  useQuery({
    queryKey: CARDS_QUERY_KEY,
    queryFn: async () => {
      const res = await useFetch.get<BankCard[]>("payment/MyCards");
      return res.data;
    },
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });

export const useBindCardInit = () =>
  useMutation({
    mutationKey: ["card-bind-init"],
    mutationFn: async (payload: BindCardInitPayload) => {
      const res = await useFetch.post<BindCardInitResponse>(
        "payment/BindCardInit",
        payload
      );
      return res.data;
    },
  });

export const useBindCardConfirm = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ["card-bind-confirm"],
    mutationFn: async (payload: BindCardConfirmPayload) => {
      const res = await useFetch.post<BankCard>(
        "payment/BindCardConfirm",
        payload
      );
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: CARDS_QUERY_KEY });
    },
  });
};

export const useDeleteCard = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ["card-delete"],
    mutationFn: async (cardId: string) => {
      await useFetch.delete(`payment/Card/${cardId}`);
      return cardId;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: CARDS_QUERY_KEY });
    },
  });
};

export const useSetDefaultCard = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ["card-set-default"],
    mutationFn: async (cardId: string) => {
      const res = await useFetch.post<BankCard>(
        `payment/SetDefaultCard/${cardId}`
      );
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: CARDS_QUERY_KEY });
    },
  });
};

export const formatCardLabel = (card: BankCard): string => {
  const vendor = card.vendor || "Bank";
  const last4 = card.masked_pan?.slice(-4) || "";
  return last4 ? `${vendor} •••• ${last4}` : vendor;
};

export const formatCardExpiry = (card: BankCard): string => {
  if (!card.expire_month || !card.expire_year) return "";
  const mm = String(card.expire_month).padStart(2, "0");
  const yy = String(card.expire_year).slice(-2);
  return `${mm}/${yy}`;
};

export interface ApiError {
  error?: string;
  message?: string;
}

// Distinct status returned by the backend so callers can swap copy
// without parsing message strings. CARD_INVALID covers Luhn/format
// rejections (Pydantic 422), RATE_LIMITED covers 429s or "too many
// requests"-shaped detail messages.
export type CardErrorKind =
  | "RATE_LIMITED"
  | "OTP_LOCKED"
  | "CARD_INVALID"
  | "CARD_DUPLICATE"
  | "PHONE_REQUIRED"
  | "OTP_INVALID"
  | "UNKNOWN";

export const classifyCardError = (err: unknown): CardErrorKind => {
  const status = (err as any)?.response?.status;
  const data = (err as any)?.response?.data;
  const detail = data?.detail;
  const errorCode =
    detail && typeof detail === "object" ? detail.error : undefined;

  // OTP brute-force lockout also rides on 429, but it carries its own
  // "too many wrong attempts" copy — classify it before the generic
  // rate-limit branch so callers can surface the attempt-specific message.
  if (errorCode === "otp_locked") return "OTP_LOCKED";
  if (status === 429) return "RATE_LIMITED";
  if (errorCode === "rate_limit_exceeded") return "RATE_LIMITED";
  if (errorCode === "card_duplicate") return "CARD_DUPLICATE";
  if (errorCode === "phone_required") return "PHONE_REQUIRED";
  if (errorCode === "invalid_card") return "CARD_INVALID";
  if (errorCode === "validation_error") return "CARD_INVALID";
  if (errorCode === "card_verification_failed") return "CARD_INVALID";
  if (errorCode === "invalid_otp") return "OTP_INVALID";

  // Pydantic validation errors arrive as `detail: [{ loc, msg, ... }]`.
  // Any of them mentioning card_number / expiry / Luhn is treated as
  // a card-shape problem.
  if (Array.isArray(detail)) {
    const cardField = detail.some((e: any) =>
      Array.isArray(e?.loc) &&
      e.loc.some(
        (p: any) =>
          p === "card_number" || p === "expire_date" || p === "pan" || p === "expiry"
      )
    );
    if (cardField) return "CARD_INVALID";
  }

  const text = typeof detail === "string" ? detail.toLowerCase() : "";
  if (text.includes("too many") || text.includes("rate limit")) {
    return "RATE_LIMITED";
  }
  if (text.includes("card") || text.includes("luhn") || text.includes("pan")) {
    return "CARD_INVALID";
  }

  return "UNKNOWN";
};

// Transport/infrastructure failures that must never reach the user as raw
// text — DNS lookups, socket errors, timeouts, or a backend that leaked a
// Python error (e.g. "[Errno -5] No address associated with hostname")
// into `detail` when an upstream gateway was unreachable.
const TECHNICAL_PATTERNS = [
  "errno",
  "hostname",
  "no address associated",
  "getaddrinfo",
  "connection",
  "timeout",
  "timed out",
  "traceback",
  "max retries",
  "failed to establish",
  "econnrefused",
  "network",
];

const looksTechnical = (msg: string): boolean => {
  const text = msg.toLowerCase();
  return TECHNICAL_PATTERNS.some((p) => text.includes(p));
};

// True when the failure is transport-level (no HTTP response) or the
// backend surfaced a raw infrastructure error string. Callers swap these
// for a friendly fallback instead of confusing technical text.
export const isTechnicalError = (err: unknown): boolean => {
  const e = err as any;
  if (e?.code === "ERR_NETWORK" || e?.message === "Network Error") return true;
  if (e?.request && e?.response === undefined) return true;
  const detail = e?.response?.data?.detail;
  const text =
    typeof detail === "string"
      ? detail
      : typeof detail?.message === "string"
        ? detail.message
        : "";
  return text ? looksTechnical(text) : false;
};

export const extractErrorMessage = (err: unknown, fallback: string): string => {
  const detail = (err as any)?.response?.data?.detail;
  if (typeof detail === "string")
    return looksTechnical(detail) ? fallback : detail;
  if (detail && typeof detail === "object" && detail.message)
    return looksTechnical(detail.message) ? fallback : detail.message;
  if (Array.isArray(detail)) {
    // Pydantic 422 — join the human-readable msgs.
    const msgs = detail
      .map((e: any) => e?.msg)
      .filter((s: unknown): s is string => typeof s === "string" && s.length > 0);
    if (msgs.length) return msgs.join("; ");
  }
  return fallback;
};
